// Mirrors the Go backend's integrations/application/dto.go and the Postgres
// enums in internal/migrations/000031_create_integration_tables.up.sql (the
// lead_webhook value was added in 000038).
export type IntegrationProvider =
  | 'facebook_page'
  | 'instagram'
  | 'webhook'
  | 'marketplace'
  | 'lead_webhook'
export type IntegrationTrigger = 'new_listing' | 'price_drop' | 'sold' | 'manual'
export type IntegrationPostStatus = 'pending' | 'sent' | 'failed' | 'skipped'

export const PROVIDER_LABELS: Record<IntegrationProvider, string> = {
  facebook_page: 'Facebook Page',
  instagram: 'Instagram',
  webhook: 'Outbound webhook',
  marketplace: 'Facebook Marketplace',
  lead_webhook: 'Lead webhook',
}

export const TRIGGER_LABELS: Record<IntegrationTrigger, string> = {
  new_listing: 'New listing',
  price_drop: 'Price drop',
  sold: 'Sold',
  manual: 'Manual',
}

export const POST_STATUS_LABELS: Record<IntegrationPostStatus, string> = {
  pending: 'Pending',
  sent: 'Sent',
  failed: 'Failed',
  skipped: 'Skipped',
}

export const POST_STATUS_VARIANT: Record<
  IntegrationPostStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  pending: 'secondary',
  sent: 'default',
  failed: 'destructive',
  skipped: 'outline',
}

// Which inventory events auto-post to a connection.
export interface AutoPostFlags {
  new_listing: boolean
  price_drop: boolean
  sold: boolean
}

export const DEFAULT_AUTO_POST: AutoPostFlags = {
  new_listing: true,
  price_drop: false,
  sold: false,
}

// Connection row. Secrets never appear — only *_set flags and non-secret
// config (page name, webhook URL) — except the lead webhook's generated
// route token, which must be pasted into the sender's form tool.
export interface IntegrationConnection {
  id: string
  provider: IntegrationProvider
  name: string
  enabled: boolean
  token_set: boolean
  secret_set: boolean
  page_name?: string
  ig_username?: string
  webhook_url?: string
  sync_leads: boolean
  webhook_token?: string
  auto_post: AutoPostFlags
  message_template: string
  created_at: string
  updated_at: string
}

export interface ConnectionWriteBody {
  provider: IntegrationProvider
  name: string
  enabled?: boolean
  url?: string
  secret?: string
  endpoint?: string
  sync_leads?: boolean
  auto_post: AutoPostFlags
  message_template: string
}

// Facebook connect flow: start returns the dialog URL; the callback returns
// a selection token + token-free page summaries to redeem when saving.
export interface FacebookStart {
  url: string
  simulation: boolean
  selection_token?: string
  pages?: ManagedPage[]
}

export interface ManagedPage {
  id: string
  name: string
  ig_username?: string
}

export interface FacebookCallback {
  selection_token: string
  pages: ManagedPage[]
}

export interface FacebookConnectionBody {
  selection_token: string
  page_id: string
  name: string
  link_instagram: boolean
  sync_leads: boolean
  auto_post: AutoPostFlags
  message_template: string
}

export interface IntegrationPost {
  id: string
  connection_id: string
  connection: string
  provider: IntegrationProvider
  boat_id: string | null
  boat_label: string
  trigger: IntegrationTrigger
  status: IntegrationPostStatus
  message: string
  external_id: string
  permalink: string
  error: string
  created_at: string
  sent_at: string | null
}

export interface ManualPostBody {
  connection_id: string
  message?: string
}
